import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApiGet, fetchApiPost } from "./utils";
import type {
  ApproveDocumentDispatchRequest,
  BulkSendDocumentDispatchRequest,
  DocumentDispatchEversignStatusReportRequest,
  HideDocumentDispatchRequest,
  RetryDocumentDispatchRequest,
  SendDocumentDispatchRequest,
} from "@/lib/validation/otcDocuments";

export const otcDocumentDispatchBaseKey = "otcDocumentDispatch";
export const nightlyDocumentDispatchBaseKey = "nightlyDocumentDispatch";
export const deferredPaymentDocumentDispatchBaseKey = "deferredPaymentDocumentDispatch";

export type DocumentDispatchFeatureType = "otc_portal_amendment" | "agtech_email_contract" | "deferred_payment_contract";
export type DocumentDispatchRequestType = "automatic_generation" | "retry";
export type OtcAmendmentChangeType = "created" | "updated";
export type DocumentPreparationStatus = "queued" | "processing" | "ready" | "failed";
export type DocumentDeliveryMode = "automatic" | "manual";
export type DocumentDeliveryStatus = "not_queued" | "queued" | "processing" | "submitted" | "failed";
export type DocumentActualSendMethod = "automatic" | "manual";
export type DocumentDispatchReviewStatus = "pending" | "approved" | "not_required";
export type DocumentDispatchVisibilityStatus = "visible" | "hidden" | "all";
export type DocumentDispatchListPreset = "automated_queued" | "manual_queued";

export type DocumentDispatchDocumentDto = {
  id: string;
  filename: string;
  contentType: string | null;
  available: boolean;
};

export type DocumentDispatchSigningDto = {
  signingSessionId: string;
  lastStatus: string;
  hasProviderSession: boolean;
  originalDocument: DocumentDispatchDocumentDto | null;
  signedDocument: DocumentDispatchDocumentDto | null;
};

export type DocumentDispatchListItemDto = {
  id: string;
  createdAt: string;
  updatedAt: string;
  featureType: DocumentDispatchFeatureType;
  featureTableId: string;
  requestNumber: number;
  requestType: DocumentDispatchRequestType;
  amendmentChangeType: OtcAmendmentChangeType | null;
  preparationStatus: DocumentPreparationStatus;
  failedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  requiresReview: boolean;
  approvedAt: string | null;
  approvedByUserId: string | null;
  deliveryMode: DocumentDeliveryMode;
  deliveryStatus: DocumentDeliveryStatus;
  actualSendMethod: DocumentActualSendMethod | null;
  sendFailedAt: string | null;
  sentAt: string | null;
  sentByUserId: string | null;
  hiddenAt: string | null;
  hiddenReason: string | null;
  hiddenByUserId: string | null;
  sendEligible: boolean;
  OtcAmendmentInfo: {
    Customer: { id: string; client_id: string; long_name: string };
    TradingContract: { id: string; contract_no: string | null };
    TradingContractLayer: { id: string; layer_no: number; amendment_type: string | null };
    amendmentNumber: number;
  } | null;
  AgtechContractInfo: {
    trader: string | null | undefined;
    contractNumber: string | null | undefined;
    amendmentNumber: string | null | undefined;
    contractDate: string | null | undefined;
    amendmentDate: string | null | undefined;
    contractType: string | null | undefined;
    location: string | null | undefined;
    Client: { id: string; client_id: string; long_name: string } | null;
    ToClient: { id: string; client_id: string; long_name: string } | null;
    SoldToClient: { id: string; client_id: string; long_name: string } | null;
    PurchasedFromClient: { id: string; client_id: string; long_name: string } | null;
    FromClient: { id: string; client_id: string; long_name: string } | null;
  } | null;
  DeferredPaymentContractInfo: {
    Customer: { id: string; client_id: string; long_name: string } | null;
    contractNumber: string;
    contractDisplayNumber: string | null;
    customerRevisionNumber: number | null;
    deferredContractDocumentInstanceId: string | null;
  } | null;
  OtcContractDocumentInstance: { id: string } | null;
  AgtechContractDocumentInstance: { id: string } | null;
  signing: DocumentDispatchSigningDto | null;
};

export type DocumentDispatchListQuery = {
  preset?: DocumentDispatchListPreset;
  feature_type: DocumentDispatchFeatureType;
  preparation_status?: DocumentPreparationStatus;
  queue_status?: DocumentDeliveryStatus;
  delivery_mode?: DocumentDeliveryMode;
  review_status?: DocumentDispatchReviewStatus;
  visibility_status?: DocumentDispatchVisibilityStatus;
  page: number;
  page_size: number;
};

export type DocumentDispatchListResult = {
  items: DocumentDispatchListItemDto[];
  page: number;
  pageSize: number;
  total: number;
};

export type DocumentDispatchPreviewResult = {
  url: string;
  expiresAt: string;
  document: DocumentDispatchDocumentDto;
};

export type DocumentDispatchEnqueueResult = {
  documentDispatchQueueEntryId: string;
  requestNumber: number;
  generationJobId: string;
};

export type ApproveDocumentDispatchResult = { documentDispatchQueueEntryId: string; approvedAt: string };
export type HideDocumentDispatchResult = { documentDispatchQueueEntryId: string; hiddenAt: string };
export type EnqueueDocumentDispatchSendResult = { documentDispatchQueueEntryId: string; sendJobId: string };
export type EnqueueBulkDocumentDispatchSendsResult = {
  enqueuedCount: number;
  queueEntryIds: string[];
  approvedQueueEntryIds: string[];
};
export type DocumentDispatchEversignStatusReportResult = {
  tracked_report_id: string;
  document_id: string;
  report_type: "document_dispatch_eversign_status";
  link: string;
};

function listQueryParams(query: DocumentDispatchListQuery): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]: [string, string | number | undefined]) => {
    if (value !== undefined) params.set(key, value.toString());
  });
  return params;
}

export function useListOtcDocumentDispatches(query: DocumentDispatchListQuery) {
  const params = listQueryParams(query);
  return useQuery({
    queryKey: [otcDocumentDispatchBaseKey, params.toString()],
    queryFn: () => fetchApiGet<DocumentDispatchListResult>("otc/document-dispatch/list", params),
    keepPreviousData: true,
    retry: 1,
  });
}

export function useListNightlyDocumentDispatches(query: DocumentDispatchListQuery) {
  const params = listQueryParams(query);
  return useQuery({
    queryKey: [nightlyDocumentDispatchBaseKey, params.toString()],
    queryFn: () => fetchApiGet<DocumentDispatchListResult>("otc/document-dispatch/list", params),
    keepPreviousData: true,
    retry: 1,
  });
}

export function useListDeferredPaymentDocumentDispatches(query: DocumentDispatchListQuery) {
  const params = listQueryParams(query);
  return useQuery({
    queryKey: [deferredPaymentDocumentDispatchBaseKey, params.toString()],
    queryFn: () => fetchApiGet<DocumentDispatchListResult>("otc/document-dispatch/list", params),
    keepPreviousData: true,
    retry: 1,
  });
}

export function useGenerateDocumentDispatchEversignStatusReport() {
  return useMutation({
    mutationFn: async (data: DocumentDispatchEversignStatusReportRequest) =>
      fetchApiPost<DocumentDispatchEversignStatusReportResult>(
        "otc/document-dispatch/reports/eversign-status/generate",
        data,
      ),
  });
}

export function getDocumentDispatchPreview(
  queueEntryId: string,
  documentType: "sent" | "signed" = "sent",
) {
  const params = new URLSearchParams({
    queue_entry_id: queueEntryId,
    document_type: documentType,
  });
  return fetchApiGet<DocumentDispatchPreviewResult>("otc/document-dispatch/preview", params);
}

export function useApproveDocumentDispatch(featureType: DocumentDispatchFeatureType = "otc_portal_amendment") {
  const queryClient = useQueryClient();
  const baseKey = featureType === "otc_portal_amendment"
    ? otcDocumentDispatchBaseKey
    : featureType === "agtech_email_contract"
      ? nightlyDocumentDispatchBaseKey
      : deferredPaymentDocumentDispatchBaseKey;
  return async (data: ApproveDocumentDispatchRequest) => {
    const result = await fetchApiPost<ApproveDocumentDispatchResult>("otc/document-dispatch/approve", data);
    await queryClient.invalidateQueries({ queryKey: [baseKey] });
    return result;
  };
}

export function useHideDocumentDispatch(featureType: DocumentDispatchFeatureType) {
  const queryClient = useQueryClient();
  const baseKey = featureType === "otc_portal_amendment"
    ? otcDocumentDispatchBaseKey
    : featureType === "agtech_email_contract"
      ? nightlyDocumentDispatchBaseKey
      : deferredPaymentDocumentDispatchBaseKey;
  return async (data: HideDocumentDispatchRequest) => {
    const result = await fetchApiPost<HideDocumentDispatchResult>("otc/document-dispatch/hide", data);
    await queryClient.invalidateQueries({ queryKey: [baseKey] });
    return result;
  };
}

export function useRetryDocumentDispatch() {
  const queryClient = useQueryClient();
  return async (data: RetryDocumentDispatchRequest) => {
    const result = await fetchApiPost<DocumentDispatchEnqueueResult>("otc/document-dispatch/retry", data);
    await queryClient.invalidateQueries({ queryKey: [otcDocumentDispatchBaseKey] });
    return result;
  };
}

export function useBulkSendDocumentDispatch(featureType: DocumentDispatchFeatureType = "otc_portal_amendment") {
  const queryClient = useQueryClient();
  const baseKey = featureType === "otc_portal_amendment"
    ? otcDocumentDispatchBaseKey
    : featureType === "agtech_email_contract"
      ? nightlyDocumentDispatchBaseKey
      : deferredPaymentDocumentDispatchBaseKey;
  return async (data: BulkSendDocumentDispatchRequest) => {
    const result = await fetchApiPost<EnqueueBulkDocumentDispatchSendsResult>("otc/document-dispatch/send/bulk", data);
    await queryClient.invalidateQueries({ queryKey: [baseKey] });
    return result;
  };
}

export function useSendDocumentDispatch(featureType: DocumentDispatchFeatureType = "otc_portal_amendment") {
  const queryClient = useQueryClient();
  const baseKey = featureType === "otc_portal_amendment"
    ? otcDocumentDispatchBaseKey
    : featureType === "agtech_email_contract"
      ? nightlyDocumentDispatchBaseKey
      : deferredPaymentDocumentDispatchBaseKey;
  return async (data: SendDocumentDispatchRequest) => {
    const result = await fetchApiPost<EnqueueDocumentDispatchSendResult>("otc/document-dispatch/send", data);
    await queryClient.invalidateQueries({ queryKey: [baseKey] });
    return result;
  };
}
